import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";

import { environment } from "../../../environments/environment";

export interface SupportAttachment {
  attachment_id: string;
  original_filename: string;
  content_type: string;
  file_size: number;
  created_at: string;
}

export interface SupportMessage {
  message_id: string;
  sender_name: string;
  body: string;
  is_staff_reply: boolean;
  created_at: string;
  attachments: SupportAttachment[];
}

export interface SupportTicket {
  ticket_id: string;
  reference: string;
  name: string;
  email: string;
  category: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  assigned_to: string | null;
  assigned_to_name: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  messages: SupportMessage[];
}

export interface CreateSupportTicketRequest {
  name: string;
  email: string;
  category: string;
  subject: string;
  description: string;
  attachment?: File | null;
  turnstileToken?: string;
}

export interface SupportTicketCreatedResponse {
  success: boolean;
  message: string;
  ticket: {
    ticket_id: string;
    reference: string;
    status: string;
    created_at: string;
  };
}

export interface SupportTicketListResponse {
  success: boolean;
  results: SupportTicket[];
}

export interface SupportTicketDetailResponse {
  success: boolean;
  ticket: SupportTicket;
}

export interface SupportMessageResponse {
  success: boolean;
  message: SupportMessage;
}

export interface PlatformSupportTicketListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SupportTicket[];
}

@Injectable({
  providedIn: "root",
})
export class SupportService {
  private readonly baseUrl = `${environment.apiUrl}/support`;
  private readonly adminUrl = `${environment.apiUrl}/platform-admin/support`;

  constructor(private http: HttpClient) {}

  createTicket(
    request: CreateSupportTicketRequest,
  ): Observable<SupportTicketCreatedResponse> {
    const formData = new FormData();

    formData.append("name", request.name);
    formData.append("email", request.email);
    formData.append("category", request.category);
    formData.append("subject", request.subject);
    formData.append("description", request.description);

    if (request.attachment) {
      formData.append("attachment", request.attachment);
    }

    if (request.turnstileToken) {
      formData.append("turnstile_token", request.turnstileToken);
    }

    return this.http.post<SupportTicketCreatedResponse>(
      `${this.baseUrl}/tickets/`,
      formData,
    );
  }

  getMyTickets(): Observable<SupportTicketListResponse> {
    return this.http.get<SupportTicketListResponse>(`${this.baseUrl}/tickets/`);
  }

  getTicket(ticketId: string): Observable<SupportTicketDetailResponse> {
    return this.http.get<SupportTicketDetailResponse>(
      `${this.baseUrl}/tickets/${ticketId}/`,
    );
  }

  replyToTicket(
    ticketId: string,
    body: string,
    attachment?: File | null,
  ): Observable<SupportMessageResponse> {
    const formData = new FormData();

    formData.append("body", body);

    if (attachment) {
      formData.append("attachment", attachment);
    }

    return this.http.post<SupportMessageResponse>(
      `${this.baseUrl}/tickets/${ticketId}/messages/`,
      formData,
    );
  }

  downloadAttachment(attachmentId: string): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/attachments/${attachmentId}/download/`,
      {
        responseType: "blob",
      },
    );
  }

  getAdminTickets(filters?: {
    status?: string;
    priority?: string;
    search?: string;
    page?: number;
  }): Observable<PlatformSupportTicketListResponse> {
    let params = new HttpParams();

    if (filters?.status) {
      params = params.set("status", filters.status);
    }

    if (filters?.priority) {
      params = params.set("priority", filters.priority);
    }

    if (filters?.search) {
      params = params.set("search", filters.search);
    }

    if (filters?.page) {
      params = params.set("page", filters.page.toString());
    }

    return this.http.get<PlatformSupportTicketListResponse>(
      `${this.adminUrl}/tickets/`,
      { params },
    );
  }

  getAdminTicket(ticketId: string): Observable<SupportTicketDetailResponse> {
    return this.http.get<SupportTicketDetailResponse>(
      `${this.adminUrl}/tickets/${ticketId}/`,
    );
  }

  updateAdminTicket(
    ticketId: string,
    changes: {
      status?: string;
      priority?: string;
      assigned_to?: string | null;
    },
  ): Observable<SupportTicketDetailResponse> {
    return this.http.patch<SupportTicketDetailResponse>(
      `${this.adminUrl}/tickets/${ticketId}/`,
      changes,
    );
  }

  replyAsAdmin(
    ticketId: string,
    body: string,
    isInternal = false,
    attachment?: File | null,
  ): Observable<SupportMessageResponse> {
    const formData = new FormData();

    formData.append("body", body);
    formData.append("is_internal", String(isInternal));

    if (attachment) {
      formData.append("attachment", attachment);
    }

    return this.http.post<SupportMessageResponse>(
      `${this.adminUrl}/tickets/${ticketId}/messages/`,
      formData,
    );
  }
}
